import React from 'react';
import { DebitNote } from '../../types';
import Input from './Input';
import Button from './Button';

interface InvoiceDebitNotesSectionProps {
  debitNotes: DebitNote[];
  newDebitNote: Partial<DebitNote>;
  onNewDebitNoteChange: (value: Partial<DebitNote>) => void;
  onAddDebitNote: () => void;
}

const InvoiceDebitNotesSection: React.FC<InvoiceDebitNotesSectionProps> = ({
  debitNotes,
  newDebitNote,
  onNewDebitNoteChange,
  onAddDebitNote,
}) => {
  return (
    <div className="border-t pt-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Debit Notes (DN)</h3>

      <div className="overflow-x-auto mb-4">
        <table className="min-w-full bg-white border border-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 border-b text-xs font-medium">Inv. No.</th>
              <th className="px-3 py-2 border-b text-xs font-medium">Date</th>
              <th className="px-3 py-2 border-b text-xs font-medium">Vessel</th>
              <th className="px-3 py-2 border-b text-xs font-medium">Description</th>
              <th className="px-3 py-2 border-b text-xs font-medium" colSpan={2}>Amount</th>
              <th className="px-3 py-2 border-b text-xs font-medium" colSpan={2}>Deduction</th>
              <th className="px-3 py-2 border-b text-xs font-medium" colSpan={2}>Paid</th>
              <th className="px-3 py-2 border-b text-xs font-medium" colSpan={2}>Net Remaining</th>
            </tr>
            <tr className="bg-gray-50">
              <th colSpan={4}></th>
              <th className="px-2 py-1 border-b text-xs">USD</th>
              <th className="px-2 py-1 border-b text-xs">EGP</th>
              <th className="px-2 py-1 border-b text-xs">USD</th>
              <th className="px-2 py-1 border-b text-xs">EGP</th>
              <th className="px-2 py-1 border-b text-xs">USD</th>
              <th className="px-2 py-1 border-b text-xs">EGP</th>
              <th className="px-2 py-1 border-b text-xs">USD</th>
              <th className="px-2 py-1 border-b text-xs">EGP</th>
            </tr>
          </thead>
          <tbody>
            {debitNotes.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-4 text-center text-sm text-gray-500 italic">No debit notes</td></tr>
            )}
            {debitNotes.map((dn) => (
              <tr key={dn.id}>
                <td className="px-3 py-2 border-b text-xs text-center">{dn.invoiceNumber}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{dn.date}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{dn.vessel}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{dn.description}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{(dn.amount?.usd || 0).toFixed(2)}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{(dn.amount?.egp || 0).toFixed(2)}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{(dn.deduction?.usd || 0).toFixed(2)}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{(dn.deduction?.egp || 0).toFixed(2)}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{(dn.paid?.usd || 0).toFixed(2)}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{(dn.paid?.egp || 0).toFixed(2)}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{(dn.netRemaining?.usd || 0).toFixed(2)}</td>
                <td className="px-3 py-2 border-b text-xs text-center">{(dn.netRemaining?.egp || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="p-4 bg-gray-50 rounded-lg">
        <h5 className="text-sm font-medium text-gray-800 mb-3">Add Debit Note</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Input label="Invoice No." value={newDebitNote.invoiceNumber || ''} onChange={(e) => onNewDebitNoteChange({...newDebitNote, invoiceNumber: e.target.value})} placeholder="INV-001" />
          <Input label="Date" type="date" value={newDebitNote.date || ''} onChange={(e) => onNewDebitNoteChange({...newDebitNote, date: e.target.value})} />
          <Input label="Vessel" value={newDebitNote.vessel || ''} onChange={(e) => onNewDebitNoteChange({...newDebitNote, vessel: e.target.value})} placeholder="Vessel" />
          <Input label="Description" value={newDebitNote.description || ''} onChange={(e) => onNewDebitNoteChange({...newDebitNote, description: e.target.value})} placeholder="Description" />
        </div>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mt-3">
          <Input label="Amt USD" type="number" step="0.01" value={newDebitNote.amount?.usd || 0} onChange={(e) => onNewDebitNoteChange({...newDebitNote, amount: {usd: parseFloat(e.target.value) || 0, egp: newDebitNote.amount?.egp || 0}})} />
          <Input label="Amt EGP" type="number" step="0.01" value={newDebitNote.amount?.egp || 0} onChange={(e) => onNewDebitNoteChange({...newDebitNote, amount: {usd: newDebitNote.amount?.usd || 0, egp: parseFloat(e.target.value) || 0}})} />
          <Input label="Ded USD" type="number" step="0.01" value={newDebitNote.deduction?.usd || 0} onChange={(e) => onNewDebitNoteChange({...newDebitNote, deduction: {usd: parseFloat(e.target.value) || 0, egp: newDebitNote.deduction?.egp || 0}})} />
          <Input label="Ded EGP" type="number" step="0.01" value={newDebitNote.deduction?.egp || 0} onChange={(e) => onNewDebitNoteChange({...newDebitNote, deduction: {usd: newDebitNote.deduction?.usd || 0, egp: parseFloat(e.target.value) || 0}})} />
          <Input label="Paid USD" type="number" step="0.01" value={newDebitNote.paid?.usd || 0} onChange={(e) => onNewDebitNoteChange({...newDebitNote, paid: {usd: parseFloat(e.target.value) || 0, egp: newDebitNote.paid?.egp || 0}})} />
          <Input label="Paid EGP" type="number" step="0.01" value={newDebitNote.paid?.egp || 0} onChange={(e) => onNewDebitNoteChange({...newDebitNote, paid: {usd: newDebitNote.paid?.usd || 0, egp: parseFloat(e.target.value) || 0}})} />
          <Input label="Net USD" type="number" step="0.01" value={newDebitNote.netRemaining?.usd || 0} onChange={(e) => onNewDebitNoteChange({...newDebitNote, netRemaining: {usd: parseFloat(e.target.value) || 0, egp: newDebitNote.netRemaining?.egp || 0}})} />
          <Input label="Net EGP" type="number" step="0.01" value={newDebitNote.netRemaining?.egp || 0} onChange={(e) => onNewDebitNoteChange({...newDebitNote, netRemaining: {usd: newDebitNote.netRemaining?.usd || 0, egp: parseFloat(e.target.value) || 0}})} />
        </div>
        <div className="mt-3">
          <Button type="button" variant="secondary" onClick={onAddDebitNote}>Add Debit Note</Button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDebitNotesSection;
